import { BodyText, Button, Heading } from '@farol-ds/react'
import {
  borderRadiusSm,
  borderWidthSm,
  colorFeedbackNegative1,
  colorFeedbackNegative2,
  spacing3xs,
} from '@farol-ds/tokens'
import { Image } from '@jusbrasil-web/shared-next-image'
import { PinInput } from '@jusbrasil-web/shared-pin-input'
import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react'

import { LoginCard } from '../login-card'
import confirmEmailIllustration from './assets/confirm-email.svg'
import styles from './email-otp-confirmation.module.scss'

export const OTP_LENGTH = 6
export const RESEND_COUNTDOWN_SECONDS = 60

// Inline, not a CSS Module class: getComputedStyle on a `.module.scss` class
// resolves to nothing under Jest (CSS Modules are mocked), so the banner's
// background is only assertable — and only paints reliably regardless of a
// caller's CSS pipeline — as an inline style built from the same tokens.
// Mirrors shared-confirmation-code-step's ERROR_BANNER_STYLE (QA2-5/QA2-8b).
const ERROR_BANNER_STYLE: CSSProperties = {
  backgroundColor: colorFeedbackNegative1,
  border: `${borderWidthSm}px solid ${colorFeedbackNegative2}`,
  borderRadius: borderRadiusSm,
  padding: spacing3xs,
}

export interface EmailOtpConfirmationProps {
  email: string
  isLoading?: boolean
  error?: string
  /** Called when the 6 digits are typed/pasted (auto-submit) or "Confirmar" is clicked. */
  onComplete: (code: string) => void
  /** Resends the OTP; resolves `true` on success so the countdown restarts. */
  onResend: () => Promise<boolean>
  /** Goes back to the e-mail step ("Alterar"). */
  onChangeEmail: () => void
  onCodeChange?: () => void
  onCloseClick?: () => void
  /** Overrides the 60s default — the entity-notice fake-door flow (QA2-5) uses 15s. */
  resendCountdownSeconds?: number
  /** 'plain' (default, LP behavior unchanged) or 'banner' — the Figma-matched negative-feedback box (QA2-5). */
  errorVariant?: 'plain' | 'banner'
}

export function EmailOtpConfirmation({
  email,
  isLoading = false,
  error,
  onComplete,
  onResend,
  onChangeEmail,
  onCodeChange,
  onCloseClick,
  resendCountdownSeconds = RESEND_COUNTDOWN_SECONDS,
  errorVariant = 'plain',
}: EmailOtpConfirmationProps) {
  const [code, setCode] = useState('')
  const [secondsLeft, setSecondsLeft] = useState(resendCountdownSeconds)
  const [isResending, setIsResending] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval>>()

  const startCountdown = useCallback(() => {
    setSecondsLeft(resendCountdownSeconds)
    if (intervalRef.current) clearInterval(intervalRef.current)
    intervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) clearInterval(intervalRef.current)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }, [resendCountdownSeconds])

  useEffect(() => {
    startCountdown()
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [startCountdown])

  const handleChange = useCallback(
    (value: string) => {
      setCode(value)
      if (error) onCodeChange?.()
    },
    [error, onCodeChange]
  )

  const handleResend = useCallback(async () => {
    if (secondsLeft > 0 || isResending) return
    setIsResending(true)
    const ok = await onResend()
    setIsResending(false)
    if (ok) startCountdown()
  }, [secondsLeft, isResending, onResend, startCountdown])

  const canResend = secondsLeft === 0 && !isResending && !isLoading

  return (
    <LoginCard onCloseClick={onCloseClick}>
      <div className={styles.content}>
        <div className={styles.headerGroup}>
          <Image
            src={confirmEmailIllustration}
            alt=""
            width={120}
            height={120}
            className={styles.illustration}
            unoptimized
          />
          <Heading size="lg" className={styles.title} asChild>
            <h1>Confirme seu e-mail</h1>
          </Heading>
          <BodyText size="lg" className={styles.subtitle}>
            Digite o código enviado para o e-mail
          </BodyText>
        </div>

        <div className={styles.emailRow}>
          <BodyText className={styles.email} asChild>
            <strong>{email}</strong>
          </BodyText>{' '}
          <Button kind="plain" className={styles.changeLink} onClick={onChangeEmail}>
            Alterar
          </Button>
        </div>

        <PinInput
          length={OTP_LENGTH}
          error={!!error}
          disabled={isLoading}
          onChange={handleChange}
          onComplete={onComplete}
        />

        {error && (
          <BodyText
            size="md"
            className={styles.errorMessage}
            style={errorVariant === 'banner' ? ERROR_BANNER_STYLE : undefined}
          >
            {error}
          </BodyText>
        )}

        <Button
          kind="primary"
          className={styles.submitButton}
          loading={isLoading}
          disabled={code.length < OTP_LENGTH || isLoading}
          onClick={() => onComplete(code)}
        >
          Confirmar
        </Button>

        <div className={styles.resendRow}>
          {canResend ? (
            <Button kind="plain" className={styles.resendLink} onClick={handleResend}>
              Reenviar código
            </Button>
          ) : (
            <BodyText size="md" className={styles.resendCountdown}>
              Reenviar código em {secondsLeft}s
            </BodyText>
          )}
        </div>
      </div>
    </LoginCard>
  )
}
